import { useState, useRef, useEffect } from "react";
import { API } from "@/lib/api";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

function sid() {
  let s = localStorage.getItem("spur_assist_sid");
  if (!s) {
    s = `as_${Math.random().toString(36).slice(2, 12)}${Date.now().toString(36)}`;
    localStorage.setItem("spur_assist_sid", s);
  }
  return s;
}

const GLITCH = "Hmm, I glitched — try that again in a few seconds.";

/** POST to the SSE endpoint and call onDelta for every token; resolves with the final reply. */
async function streamReply(message, onDelta) {
  const res = await fetch(`${API}/assistant/chat/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ session_id: sid(), message }),
  });
  if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let full = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const frames = buf.split("\n\n");
    buf = frames.pop() || "";
    for (const frame of frames) {
      const line = frame.split("\n").find((l) => l.startsWith("data: "));
      if (!line) continue;
      const ev = JSON.parse(line.slice(6));
      if (ev.error) throw new Error(ev.error);
      if (ev.delta) {
        full += ev.delta;
        onDelta(full);
      }
      if (ev.done) return ev.reply ?? full;
    }
  }
  return full;
}

export default function LaunchAssistant() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", text: "Hey! I'm your launch guide. Ask me anything — costs, minting, pools, going viral. 🚀" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, open]);

  const patchLast = (patch) => setMsgs((m) => [...m.slice(0, -1), { ...m[m.length - 1], ...patch }]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }, { role: "assistant", text: "", streaming: true }]);
    setBusy(true);
    try {
      const reply = await streamReply(text, (partial) => patchLast({ text: partial }));
      patchLast({ text: reply || GLITCH, streaming: false });
    } catch (e) {
      patchLast({ text: GLITCH, streaming: false });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {open && (
        <div
          className="fixed bottom-24 right-5 z-[70] w-[340px] max-w-[calc(100vw-40px)] border-2 border-[#cfff04] bg-[#121212] flex flex-col shadow-2xl"
          data-testid="assistant-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#3a3a3a]">
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#cfff04]">// Launch assistant</span>
            <button onClick={() => setOpen(false)} data-testid="assistant-close" className="text-[#d0d0d0] hover:text-white">
              <X size={14} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[380px]" data-testid="assistant-messages">
            {msgs.map((m, i) => (
              <div
                key={`am-${i}`}
                data-testid={m.role === "assistant" ? (m.streaming ? "assistant-reply-streaming" : "assistant-reply") : "assistant-user-msg"}
                className={`text-sm leading-relaxed px-3 py-2 max-w-[85%] whitespace-pre-wrap ${
                  m.role === "user"
                    ? "ml-auto bg-[#cfff04] text-[#050505]"
                    : "bg-[#141414] text-[#d4d4d4] border border-[#3a3a3a]"
                }`}
              >
                {m.text.replace(/\*\*/g, "").replace(/^[-*] /gm, "· ")}
                {m.streaming && (
                  <span className="inline-block w-2 h-3.5 ml-0.5 align-middle bg-[#cfff04] live-dot" aria-hidden="true" />
                )}
              </div>
            ))}
            {busy && !msgs[msgs.length - 1]?.text && <Loader2 size={14} className="animate-spin text-[#cfff04]" />}
            <div ref={endRef} />
          </div>
          <div className="flex items-center gap-2 p-3 border-t border-[#3a3a3a]">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="e.g. how much does a pool cost?"
              data-testid="assistant-input"
              className="flex-1 bg-[#0c0c0c] border border-[#3a3a3a] px-3 py-2 text-sm text-white focus:border-[#cfff04] outline-none"
            />
            <button onClick={send} disabled={busy} data-testid="assistant-send" className="btn-brutal bg-[#cfff04] text-[#050505] p-2 disabled:opacity-50">
              <Send size={14} />
            </button>
          </div>
        </div>
      )}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="assistant-bubble"
        className="fixed bottom-5 right-5 z-[70] btn-brutal bg-[#cfff04] text-[#050505] p-4 shadow-2xl"
        aria-label="Launch assistant"
      >
        <MessageCircle size={20} />
      </button>
    </>
  );
}
